import Main from "niteroi/packages/air-passenger-rights/controllers/Main.controller";
import {SearchField$LiveChangeEvent} from "sap/m/SearchField";
import Table from "sap/m/Table";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import ListBinding from "sap/ui/model/ListBinding";
import {Route$PatternMatchedEvent} from "sap/ui/core/routing/Route";
import {ListItemBase$PressEvent} from "sap/m/ListItemBase";
import {Button$PressEvent} from "sap/m/Button";
import Toolbar from "sap/m/Toolbar";
import SimpleForm from "sap/ui/layout/form/SimpleForm";
import Page from "sap/m/Page";
import Ui5File from "sap/ui/core/util/File";
import {ILoadDataResponse} from "niteroi/packages/system/models/interfaces";

export default class Passengers extends Main {
    private readonly onPatternMatchedHandler = this.onPatternMatched.bind(this);

    public onInit(): void {
        super.onInit();
        const router = this.getRouter();
        const routeName = this.getCurrentRouteName(router);
        if (routeName) {
            const route = router.getRoute(routeName);

            if (route.getPattern().includes("passengers/add")) {
                this.resetOriginalModel("passenger");
            } else {
                route.detachPatternMatched(this.onPatternMatchedHandler);
                route.attachPatternMatched(this.onPatternMatchedHandler);
            }
        }
    }

    public onBeforeRendering(): void {
        this.setActiveTileByRoute();
    }

    protected onPatternMatched(event: Route$PatternMatchedEvent): void {
        this.resetOriginalModel("passenger");
        const params = event.getParameters();
        const id = (params.arguments as { id: string }).id ?? "";
        this.getData(id);
    }

    protected getData(objectId: string = ""): void {
        const url = objectId ? `/api/air-passenger-rights/passengers/${objectId}` : "/api/air-passenger-rights/passengers";
        const modelName = objectId ? "passenger" : "passengers";
        this.fetchData(url, modelName, objectId);
    }

    /**
     * Search handler for passengers table
     * Filters passengers by first name, last name, email, and a custom full-name match (case-insensitive)
     * @param event - The search event containing the search query
     */
    public onListSearch(event: SearchField$LiveChangeEvent): void {
        const searchQuery = event.getParameter("newValue");
        const passengersTable = this.byId("idPassengersTable") as Table;

        if (!passengersTable) {
            return;
        }

        const binding = passengersTable.getBinding("items") as ListBinding;
        if (!binding) {
            return;
        }

        // If search query is empty, reset to default filters
        if (!searchQuery || searchQuery.trim() === "") {
            binding.filter([]);
            return;
        }

        // Create filters for each searchable field with case-insensitive Contains
        const searchFilters: Filter[] = [
            new Filter("firstName", FilterOperator.Contains, searchQuery),
            new Filter("lastName", FilterOperator.Contains, searchQuery),
            new Filter("email", FilterOperator.Contains, searchQuery),
        ];

        const customFilter = new Filter({
            path: "",
            test: (value: {firstName: string, lastName: string}): boolean => {
                if (!value) {
                    return false;
                }
                const normalizedQuery = searchQuery.toLowerCase().replace(/\s+/g, "");

                const firstName = (value.firstName || "").toString().toLowerCase();
                const lastName = (value.lastName || "").toString().toLowerCase();
                const passengerName = firstName + lastName;

                return (
                    passengerName.includes(normalizedQuery) ||
                    firstName.includes(normalizedQuery) ||
                    lastName.includes(normalizedQuery)
                );
            }
        });

        // Combine all search filters with OR logic
        const combinedSearchFilter = new Filter({
            filters: [...searchFilters, customFilter],
            and: false // OR condition - match any field
        });

        // Keep the base filters for valid codes (AND with search)
        const baseFilters = [
            new Filter("firstName", FilterOperator.NE, null),
            new Filter("lastName", FilterOperator.NE, null)
        ];

        // Combine base filters with search filter using AND logic
        const finalFilters = [
            ...baseFilters,
            combinedSearchFilter
        ];

        binding.filter(finalFilters);
    }

    public onPressColumnListItemDetail(event: ListItemBase$PressEvent): void {
        this.resetOriginalModel("passenger");
        const source = event.getSource();
        const bindingContext = source.getBindingContext("passengers");
        const selectedListItem = bindingContext.getModel().getProperty(bindingContext.getPath());
        this.navTo("air-passenger-rights/passengers/edit", {id: selectedListItem.passengerId}, false);
    }

    public onPressButtonDiscardFormEdit(): void {
        this.discardChanges("passenger");
    }

    public onPressMenuItemAdd(): void {
        this.resetOriginalModel("passenger");
        this.navTo("air-passenger-rights/passengers/add", {}, false);
    }

    public onPressButtonSaveFormEdit(event: Button$PressEvent): void {
        const source = event.getSource();
        const page = ((source.getParent() as Toolbar).getParent() as SimpleForm).getParent() as Page;
        page.setBusy(true);
        const model = this.getModel("passenger");
        if (JSON.stringify(this.originalData) !== model.getJSON()) {
            const passengerId = model.getProperty("/passengerId") as string;
            const method = passengerId ? "PUT" : "POST";
            const requestModel = this.createRequestModel();
            const url = passengerId ? `/api/air-passenger-rights/passengers/${passengerId}` : "/api/air-passenger-rights/passengers";
            const headers = this.getRequestHeaders();
            requestModel.loadData(url, model.getJSON(), true, method, false, true, headers).then(() => {
                model.setData(JSON.parse(requestModel.getJSON()));
                this.showToast(this.getText("msgPassengerSavedSuccess"));
            }).catch((response: ILoadDataResponse) => {
                const responseText = JSON.parse(response.responseText);
                this.showToast(`${responseText.code} - ${responseText.error}`);
            }).finally(() => {
                page.setBusy(false);
            });
        } else {
            page.setBusy(false);
        }
    }

    public onPressMenuItemExportJson(): void {
        this.exportToJSON("passengers");
    }

    public onPressDownloadTemplateBtn(): void {
        // Template based on passengers structure
        const template = [{
            "firstName": "Luis",
            "lastName": "Alvaro Gomes da Fonseca",
            "email": "luis.alvarogf987@gmail.com"
        }];
        Ui5File.save(JSON.stringify(template, null, 4), "passengers_template", "json", "application/json", "utf-8");
    }
}
